/**
 * The seller — who they are, how to reach them, and whether we may.
 *
 * ── The number is masked on the screen and whole in the link ─────────────────
 *
 * `sellers.ts` returns a `masked` value and, separately, an `action.href` that
 * `resolveChannel` built. This panel renders the mask and puts the whole
 * number only inside the `tel:` / `sms:` / `wa.me` href, where a tap uses it
 * and an over-the-shoulder glance does not. That is the shape the repo rule
 * asks for: a seller's phone number is not something to print on a surface
 * somebody may hand to another person.
 *
 * ── A refused channel has no link, not a disabled one ────────────────────────
 *
 * §17.5: a `do_not_use` handle comes back with `action: null`, and a client
 * cannot render a link that is not there. So the refusal is shown as text and
 * there is nothing to tap — rather than a greyed button somebody will try
 * twice and then work around.
 *
 * ── Staleness is derived, never stored ───────────────────────────────────────
 *
 * CLAUDE.md rule 15: prefer the clock to a status column. "Awaiting a reply
 * for nine days" comes from the last outbound and the last inbound rows, both
 * of which already exist. A `stale` flag would only advance when somebody
 * remembered to advance it.
 */

import { useCallback } from 'react'
import Icon, { type IconName } from '../Icon'
import { useLocale, type Locale } from '../../lib/i18n'
import { dateText, TONES } from '../../lib/format'
import { humanise } from '../../lib/domain'
import { Chip, Empty, Line, Panel, S, toneStyle } from './ui'
import SellerTimeline from './SellerTimeline'
import type { SellerHandle, SellerPayload } from './types'

const EN = {
  title: 'Seller',
  none: 'No seller is linked to this one yet.',
  unavailable: 'The seller could not be loaded.',
  awaiting: 'Waiting on a reply',
  silent: 'days since we last reached out',
  lastOut: 'We last wrote',
  lastIn: 'They last wrote',
  suggestDormant: 'Nothing has come back in a long time. Consider marking this seller dormant.',
  followUp: 'Follow up on',
  cannotContact: 'Cannot be contacted on this',
  consent: 'Consent',
  call: 'Call',
  message: 'Message',
  open: 'Open',
} as const

type CopyKey = keyof typeof EN

const ES: Record<CopyKey, string> = {
  title: 'Vendedor',
  none: 'Todavía no hay un vendedor ligado a esto.',
  unavailable: 'No se pudo cargar el vendedor.',
  awaiting: 'Esperando contestación',
  silent: 'días desde que escribimos',
  lastOut: 'Nosotros escribimos',
  lastIn: 'Ellos escribieron',
  suggestDormant: 'Hace mucho que no contestan. Considera marcar a este vendedor como inactivo.',
  followUp: 'Dar seguimiento el',
  cannotContact: 'No se puede contactar por aquí',
  consent: 'Consentimiento',
  call: 'Llamar',
  message: 'Escribir',
  open: 'Abrir',
}

const COPY: Record<Locale, Record<CopyKey, string>> = { en: EN, 'es-PR': ES }

const PHONE_KINDS = new Set(['phone', 'sms', 'whatsapp'])

function iconFor(kind: string): IconName {
  return PHONE_KINDS.has(kind) ? 'phone' : 'globe'
}

export default function SellerCard({ seller, failed = false }: { seller: SellerPayload | null; failed?: boolean }) {
  const locale = useLocale()
  const t = useCallback((key: CopyKey) => COPY[locale][key] ?? EN[key], [locale])

  const handles = [...(seller?.seller.handles ?? [])].sort((a, b) => a.sortRank - b.sortRank)

  return (
    <Panel
      id="seller"
      title={t('title')}
      defaultOpen
      badge={
        seller?.stale.awaitingReply ? (
          <Chip tone={seller.stale.suggestDormant ? TONES.warn : TONES.quiet}>{t('awaiting')}</Chip>
        ) : undefined
      }
    >
      {failed ? (
        <Empty>{t('unavailable')}</Empty>
      ) : !seller ? (
        <Empty>{t('none')}</Empty>
      ) : (
        <>
          <div style={{ ...S.big, marginTop: 2 }}>{seller.seller.name}</div>

          <div style={{ marginTop: 10 }}>
            {handles.length === 0 ? (
              <Empty>{t('none')}</Empty>
            ) : (
              handles.map((handle) => <HandleRow key={handle.id} handle={handle} t={t} />)
            )}
          </div>

          <div style={{ marginTop: 12 }}>
            {seller.stale.lastOutboundOn && (
              <Line label={t('lastOut')} first>
                {dateText(seller.stale.lastOutboundOn, locale)}
              </Line>
            )}
            {seller.stale.lastInboundOn && <Line label={t('lastIn')}>{dateText(seller.stale.lastInboundOn, locale)}</Line>}
            {seller.stale.daysSilent !== null && seller.stale.awaitingReply && (
              <Line label={t('silent')}>{seller.stale.daysSilent}</Line>
            )}
            {seller.seller.followUpOn && (
              <Line label={t('followUp')}>{dateText(seller.seller.followUpOn, locale)}</Line>
            )}
          </div>

          {seller.stale.suggestDormant && <p style={{ ...S.note, color: 'var(--warn)' }}>{t('suggestDormant')}</p>}

          <SellerTimeline rows={seller.timeline} />
        </>
      )}
    </Panel>
  )
}

function HandleRow({ handle, t }: { handle: SellerHandle; t: (key: CopyKey) => string }) {
  const label = handle.label ?? humanise(handle.kind)
  const verb = PHONE_KINDS.has(handle.kind) ? (handle.kind === 'phone' ? t('call') : t('message')) : t('open')

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: '1px solid var(--line)' }}>
      <Icon name={iconFor(handle.kind)} size={18} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 14, fontWeight: 600 }}>{handle.masked}</span>
        <span style={{ display: 'block', fontSize: 12, color: 'var(--muted)' }}>{label}</span>
      </span>
      {handle.action ? (
        <a
          href={handle.action.href}
          style={{ ...S.button, ...toneStyle(TONES.brand), textDecoration: 'none', minHeight: 44 }}
        >
          {verb}
        </a>
      ) : (
        <span style={{ fontSize: 12, color: 'var(--muted)', textAlign: 'right', maxWidth: 140 }}>
          {t('cannotContact')}
          {handle.refusal ? ` — ${humanise(handle.refusal)}` : ''}
        </span>
      )}
    </div>
  )
}
